import type { DomainEntity } from '@web-config-app/core';
import { isDomainEntity } from '@web-config-app/core';
import { useEntitySchema } from '../use-entity-schema/use-entity-schema';
import { useEntityConfig } from '../use-entity-config/use-entity-config';

interface UseResolvedEntityProps {
  entityId: string | undefined;
  schemaPath?: string;
}

/**
 * Loads an entity by id from the tenant's `entityConfigs` map and merges in the schema fetched
 * from the schemas endpoint. The returned entity is ready to use: it carries `endpoints`
 * (pre-filtered at tenant-config build time for CACT-BE entities, or shipped inline in the
 * registry yaml for entities with `isUsingConfigAppBackend: false`)
 * and `schema`, so callers don't need to wrap it further unless they specifically need the
 * config-entity form schema (in which case use `useConfigEntity`).
 */
export const useResolvedEntity = ({
  entityId,
  schemaPath = '/schemas',
}: UseResolvedEntityProps): {
  entity: DomainEntity | null;
  isError: boolean;
} => {
  const entityConfig = useEntityConfig({ entityId });
  const { schema: entitySchema, isError: isSchemaError } = useEntitySchema({
    entityId,
    schemaPath,
  });

  // Entity not found in config or schema failed to load — distinguish
  // from "still loading" so callers can show an error page.
  const isError = (!!entityId && !entityConfig) || isSchemaError;

  if (!entityConfig) {
    return { entity: null, isError };
  }

  const candidate = {
    ...entityConfig,
    schema: entitySchema,
  };

  return { entity: isDomainEntity(candidate) ? candidate : null, isError };
};
